import React, { PureComponent } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { MessageCard, InvoiceTile, Button, CurrentTransaction, Text, Theme, Container, Content, ContentBlock } from '@simplybenefits/sb-ui-library';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import { getInvoice, resetInvoiceToPending } from '../../../actions/users.js';
import { getPdf } from '../../../actions/master.js';

const benefits = {
    'life': {
        title: 'Life',
        volumeType: 'number'
    },
    'add': {
        title: 'AD&D',
        volumeType: 'number'
    },
    'ltd': {
        title: 'LTD',
        volumeType: 'number'
    },
    'std': {
        title: 'STD',
        volumeType: 'number'
    },
    'depLife': {
        title: 'Dep Life',
        volumeType: 'text'
    },
    'ci': {
        title: 'CI',
        volumeType: 'text'
    },
    'ciSpouse': {
        title: 'CI (Spouse)',
        volumeType: 'text'
    },
    'ciChild': {
        title: 'CI (Child)',
        volumeType: 'text'
    },
    'health': {
        title: 'Health',
        volumeType: 'text'
    },
    'outOfCountry': {
        title: 'Out Of Country',
        volumeType: 'text'
    },
    'stopLoss': {
        title: 'Stop Loss',
        volumeType: 'text'
    },
    'hsa': {
        title: 'HSA',
        volumeType: 'text'
    },
    'lsa': {
        title: 'LSA',
        volumeType: 'text'
    },
    'costPlus': {
        title: 'Cost Plus',
        volumeType: 'text'
    },
    'wello': {
        title: 'Wello',
        volumeType: 'text'
    },
    'virtualHealthcare': {
        title: 'Virtual Healthcare',
        volumeType: 'text'
    },
    'financialAwareness': {
        title: 'Financial Awareness',
        volumeType: 'text'
    },
    'efap': {
        title: 'EFAP',
        volumeType: 'text'
    },
    'dental': {
        title: 'Dental',
        volumeType: 'text'
    },
    'voluntaryBenefits': {
        title: 'Voluntary Benefits',
        volumeType: 'dash'
    },
    'optionalBenefits': {
        title: 'Optional',
        volumeType: 'dash'
    },
    'miscPremiums': {
        title: 'Misc',
        volumeType: 'dash'
    },
    'totals': {
        title: 'Totals',
        volumeType: 'number'
    }
};

class Invoices extends PureComponent {
    state = {
        invoice: null
    };

    _selectInvoice = async (selectedInvoice) => {
        const invoice = await this.props.dispatch(getInvoice(this.props.meta.userID, selectedInvoice.invoiceNum));

        this.setState({
            invoice: {
                ...invoice,
                meta: {
                    ...selectedInvoice,
                    ...invoice.meta
                }
            }
        });
    };

    _resetInvoiceToPending = () => {
        this.props.dispatch(resetInvoiceToPending(this.props.meta.userID, this.state.invoice.meta.invoiceNum));
    };

    _showHistory = () => {
        this.setState({
            invoice: null
        });
    };

    _downloadPDF = async (userID, invoiceNum) => {
        const fileName = `Invoice-${ invoiceNum }`;
        await this.props.dispatch(getPdf("MemberInvoice", fileName, { userID, invoiceNum }));
    };

    /**
     *
     * @param {*} number
     */
    formatNumber = (number) => `$${ (number || 0).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') }`;

    /**
     *
     * @param {*} benefit
     * @param {*} transaction
     * @returns
     */
    _determineWhetherToShow = (benefit) => {
        if (benefit === "wello" && typeof this.state.invoice.amounts[benefit]?.premium !== "number") {
            return null;
        }

        if (benefit === "marketplace" && this.state.invoice.amounts.voluntaryBenefits && this.state.invoice.amounts.voluntaryBenefits.premium !== 0) {
            return null;
        }

        if (benefit === "voluntaryBenefits" && this.state.invoice.amounts.marketplace && this.state.invoice.amounts.marketplace.premium !== 0) {
            return null;
        }

        return (
            (benefit !== "totals") &&
        (this.state.invoice.amounts[benefit]?.premium > 0 || (this.state.invoice.amounts[benefit]?.adjustments instanceof Object ?
            this.state.invoice.amounts[benefit]?.adjustments.total
            :
            this.state.invoice.amounts[benefit]?.adjustments !== 0))
        );
    };

    /**
     *
     * @param {*} x
     */
    _numberWithCommas = (x) => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // todo: move this function to backend
    _downloadCSV = (invoiceNum) => {
        let items = [];

        // eslint-disable-next-line no-unused-vars, array-callback-return
        Object.keys(benefits).map((benefit, i) => {
            const benefitParams = benefits[benefit];
            let volume = '-';
            let rate = 0;
            let premium = 0;
            let adj = 0;
            let HST = 0;
            let GST = 0;
            let PST = 0;
            let RST = 0;

            if (this.state.invoice.amounts[benefit]) {
                // this first block is to support very old legacy invoices
                if (((benefit === 'optionalBenefits') || benefit === 'miscPremiums') && this.state.invoice.amounts[benefit].totals) {
                    premium = this.state.invoice.amounts[benefit].totals.premium;
                    HST = this.state.invoice.amounts[benefit].totals.HST;
                    GST = this.state.invoice.amounts[benefit].totals.GST;
                    PST = this.state.invoice.amounts[benefit].totals.PST;
                    RST = this.state.invoice.amounts[benefit].totals.RST || 0;
                    adj = this.state.invoice.amounts[benefit].totals.adjustments;
                    rate = this.state.invoice.amounts[benefit].rate;
                } else {
                    premium = this.state.invoice.amounts[benefit].premium;
                    HST = this.state.invoice.amounts[benefit].HST;
                    GST = this.state.invoice.amounts[benefit].GST;
                    PST = this.state.invoice.amounts[benefit].PST;
                    RST = this.state.invoice.amounts[benefit].RST || 0;
                    rate = this.state.invoice.amounts[benefit].rate;

                    if (this.state.invoice.amounts[benefit].adjustments instanceof Object) {
                        adj = this.state.invoice.amounts[benefit].adjustments.net;
                        // need to add the adjustment taxes to the overall benefit taxes
                        HST += this.state.invoice.amounts[benefit].adjustments.HST;
                        GST += this.state.invoice.amounts[benefit].adjustments.GST;
                        PST += this.state.invoice.amounts[benefit].adjustments.PST;
                        RST += this.state.invoice.amounts[benefit].adjustments.RST || 0;
                    } else {
                        adj = this.state.invoice.amounts[benefit].adjustments;
                    }

                    if (benefitParams.volumeType === 'number') {
                        volume = this._numberWithCommas(Math.ceil(this.state.invoice.amounts[benefit].volume));
                    } else if (benefitParams.volumeType === 'text') {
                        volume = this.state.invoice.amounts[benefit].coverageType;
                    }
                }
            }

            if (this._determineWhetherToShow(benefit)) {
                items.push({ 
                    "benefit": benefitParams.title === `Wello` ? "Maple" : benefitParams.title,
                    "volume": volume,
                    "rate": this.formatNumber(rate),
                    "premium": this.formatNumber(premium),
                    "adj": this.formatNumber(adj),
                    "gst/hst": this.formatNumber(GST + HST),
                    "pst": this.formatNumber(PST + RST),
                    "totals": this.formatNumber(premium + GST + HST + PST + RST + adj)
                });
            }
        });

        items.push({
            "benefit": "Carry Forward",
            "volume": "-",
            "rate": "-",
            "premium": "-",
            "adj": "-",
            "gst/hst": "-",
            "pst": "-",
            "totals": this.formatNumber(this.state.invoice.amounts.totals.carryForward)
        });

        if (this.state.invoice.amounts.totals.CCFee) {
            items.push({
                "benefit": "Convenience Fee",
                "volume": "-",
                "rate": "-",
                "premium": "-",
                "adj": "-",
                "gst/hst": "-",
                "pst": "-",
                "totals": this.formatNumber(this.state.invoice.amounts.totals.CCFee)
            });
        }

        items.push({
            "benefit": "Totals",
            "volume": "-",
            "rate": "-",
            "premium": this.formatNumber(this.state.invoice.amounts.totals.premium),
            "adj": this.state.invoice.amounts.totals.adjustments instanceof Object ?
                this.formatNumber(this.state.invoice.amounts.totals.adjustments.net) :
                this.formatNumber(this.state.invoice.amounts.totals.adjustments),
            "gst/hst": this.formatNumber(this.state.invoice.amounts.totals.HST + this.state.invoice.amounts.totals.GST),
            "pst": this.formatNumber(this.state.invoice.amounts.totals.PST + (this.state.invoice.amounts.totals.RST || 0)),
            "totals": this.formatNumber(this.state.invoice.amounts.totals.grandTotal + this.state.invoice.amounts.totals.carryForward)
        });

        let title = `Invoice-${ invoiceNum }`;

        if (this.props.dateRange) {
            title += `(${ this.props.dateRange.startDate } - ${ this.props.dateRange.endDate })`;
        }

        const options = {
            title,
            fieldSeparator: ',',
            filename: `${ invoiceNum }-${ new Date().toLocaleDateString() }`,
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: true,
            useTextFile: false,
            useBom: true,
            headers: [ "Benefit/Service", "Volume", "Rate", "Premium", "Adj.", "GST/HST", "PST", "Totals" ]
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(items);
    };

    render() {
        const styles = {
            invoiceHistoryContainer: {
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'row'
            },
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center'
            }
        };

        if (this.state.invoice) {
            return (
                <Container>
                    <Content>
                        <div style={ styles.titleBlock }>
                            <Text
                                large
                                bold
                                color={ Theme.colors.mainTextGrey }
                                margin={ "10px 0 20px 0" }
                                customStyle={{ flex: '1' }}
                            >
                                { `Invoice: ${ dates.getLocalDisplay(this.state.invoice.meta.dateAdded, 'MMMM YYYY') }` }
                            </Text>

                            <Button
                                id="view-history"
                                color="primary"
                                customStyle={{
                                    height: 50,
                                    width: 180,
                                    textTransform: "capitalize",
                                    fontSize: 16
                                }}
                                onClick={ this._showHistory }
                            >
                                View History
                            </Button>
                        </div>

                        <ContentBlock>
                            
                            <Button
                                id="view-history"
                                transparent
                                customStyle={{
                                    height: 50,
                                    textTransform: "capitalize",
                                    fontSize: 16,
                                    paddingLeft: "10px",
                                    paddingRight: "10px",
                                    mrginRight: "10px"
                                }}
                                onClick={ () => this._downloadPDF(this.props.meta.userID, this.state.invoice.meta.invoiceNum) }
                            >
                                Download Invoice PDF
                            </Button>

                            <Button
                                id="view-history"
                                transparent
                                customStyle={{
                                    height: 50,
                                    textTransform: "capitalize",
                                    fontSize: 16,
                                    paddingLeft: "10px",
                                    paddingRight: "10px"
                                }}
                                onClick={ () => this._downloadCSV(this.state.invoice.invoiceNum) }
                            >
                                Download Invoice CSV
                            </Button>

                            <CurrentTransaction transaction={ this.state.invoice } isMemberInvoice={ true } />

                            <Button
                                color="white"
                                noHover
                                customStyle={{
                                    height: 60,
                                    width: 220,
                                    textTransform: "capitalize",
                                    fontSize: 16,
                                    marginLeft: 'auto'
                                }}
                                onClick={ this._resetInvoiceToPending }
                            >
                                Reset to Pending
                            </Button>
                        </ContentBlock>
                    </Content>
                </Container>
            );
        }

        return (
            <Container>
                <Content>
                    {
                        this.props.invoices.length === 0 ?
                            <MessageCard
                                title="No Invoice History"
                                subtitle="You have not been charged for any benefits yet. Invoices are generated on the 1st of every month."
                            />
                            :
                            <div style={ styles.invoiceHistoryContainer }>
                                {
                                    this.props.invoices.map((invoice, i) => (
                                        <InvoiceTile
                                            testid={i === 0 ? 'invoice-history-tile' : undefined}
                                            key={invoice.dateAdded}
                                            date={invoice.dateAdded}
                                            total={ invoice.grandTotal }
                                            status={ invoice.status }
                                            isFirstInvoice={ i === (this.props.invoices.length - 1) }
                                            onClick={() => this._selectInvoice(invoice)}
                                        />
                                    ))
                                }
                            </div>
                    }
                </Content>

                <span id="claims" />
            </Container>
        );
    }
}

Invoices.propTypes = {
    invoices: PropTypes.array
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    invoices: state.users.currentUser.invoices
});

export default (connect(mapStateToProps)(Invoices));